'use client';
import { useRef, useState } from 'react';
import type { ChangeEventHandler, SVGProps } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

export interface NumberStepperProps {
  onDecrement(): void;

  onIncrement(): void;

  onChange?(value: number): void;

  value: number;
  disabled?: boolean;
}

export interface StepperButtonProps {
  Icon(props: SVGProps<SVGSVGElement>): JSX.Element;

  onClick(): void;

  title: string;
  disabled?: boolean;
}

export function StepperButton({ Icon, disabled, ...rest }: StepperButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled}
      className={`flex justify-between items-center  text-neutral-0 bg-grey-base rounded-full p-2 my-1 hover:bg-grey-base ${
        disabled ? 'opacity-20' : ''
      }`}
    >
      <Icon className="h-3 w-3" />
    </button>
  );
}

export function NumberStepper({
  onDecrement,
  onIncrement,
  onChange,
  disabled,
  value = 1,
}: NumberStepperProps) {
  const [optimisticValue, setOptimisticValue] = useState(1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleDecrement = () => {
    setOptimisticValue((old) => old - 1);
    onDecrement();
  };
  const handleIncrement = () => {
    setOptimisticValue((old) => old + 1);
    onIncrement();
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (onChange) {
      const value = Number(e.target.value);
      setOptimisticValue(value);
      onChange(value);
    }
  };

  const handleSelect = () => {
    const input = inputRef.current;
    if (input) {
      input.select();
    }
  };

  return (
    <div className="inline-flex bg-neutral-80 rounded-md mt-5 items-center px-1 focus-within:ring-2 ring-text-primary border-2 border-white">
      <StepperButton
        onClick={handleDecrement}
        title="En mindre"
        Icon={MinusIcon}
        disabled={value < 1}
      />
      <input
        type="number"
        value={value}
        className="bg-neutral-80 text-neutral-0 appearance-none w-6 text-center border-none outline-none mx-1"
        onChange={handleChange}
        onClick={handleSelect}
        disabled={disabled}
        ref={inputRef}
        min={0}
        style={{
          MozAppearance: 'textfield',
        }}
      />
      <StepperButton onClick={handleIncrement} title="En mer" Icon={PlusIcon} />
    </div>
  );
}

export default NumberStepper;
